import React, { useEffect, useState, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle, ArrowRight, ChevronDown } from "lucide-react";

const SnakeFlowGallery = ({ items = [] }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const intervalRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const checkScreen = () => setIsMobile(window.innerWidth < 768);
    checkScreen();
    window.addEventListener("resize", checkScreen);
    return () => window.removeEventListener("resize", checkScreen);
  }, []);

  // Clean interval function
  const stopAutoPlay = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startAutoPlay = useCallback(() => {
    if (intervalRef.current) stopAutoPlay();
    if (activeStep >= items.length - 1) return;

    intervalRef.current = setInterval(() => {
      setActiveStep(prev => {
        if (prev < items.length - 1) return prev + 1;
        stopAutoPlay();
        return prev;
      });
    }, 2500);
  }, [items.length, activeStep, stopAutoPlay]);

  // Auto-play management
  useEffect(() => {
    if (items.length === 0) return;
    startAutoPlay();
    return () => stopAutoPlay();
  }, [items.length, startAutoPlay, stopAutoPlay]);

  // Reset when items change
  useEffect(() => {
    setActiveStep(0);
  }, [items]);

  const handleStepClick = useCallback((index) => {
    if (index === activeStep) return;
    setActiveStep(index);
    stopAutoPlay();
    // Restart auto-play after delay
    setTimeout(() => {
      if (activeStep < items.length - 1) {
        startAutoPlay();
      }
    }, 5000);
  }, [activeStep, items.length, stopAutoPlay, startAutoPlay]);

  const rows = [];
  for (let i = 0; i < items.length; i += 3) {
    rows.push(items.slice(i, i + 3));
  }

  const StepCard = ({ item, index, isActive }) => (
    <div
      onClick={() => handleStepClick(index)}
      className={`
        relative p-5 rounded-2xl border transition-all duration-300 cursor-pointer
        ${isActive
          ? "bg-white dark:bg-card border-primary shadow-lg scale-[1.02]"
          : "bg-white dark:bg-card/80 border-border/50 hover:border-primary/30 hover:shadow-md"
        }
      `}
      style={{
        transitionProperty: 'all',
        transitionTimingFunction: 'cubic-bezier(0.2, 0.9, 0.4, 1.1)'
      }}
    >
      <div className="relative h-40 mb-4 bg-gray-50 dark:bg-muted/20 rounded-xl overflow-hidden border border-border/30">
        {item.image ? (
          <img
            src={item.image}
            alt={item.title || "Step visual"}
            className="w-full h-full object-contain p-3 transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground/20 font-black tracking-tighter uppercase italic text-sm">
            Visual
          </div>
        )}
        <div className={`
          absolute top-3 left-3 w-8 h-8 rounded-lg flex items-center justify-center font-black text-xs shadow-md backdrop-blur-sm border
          ${isActive
            ? "bg-primary text-primary-foreground border-primary/30"
            : "bg-white dark:bg-card/90 text-muted-foreground border-border"
          }
        `}>
          {(index + 1).toString().padStart(2, '0')}
        </div>
      </div>
      <h4 className={`
        font-bold font-heading text-base mb-1 tracking-tight line-clamp-1
        ${isActive ? "text-foreground" : "text-muted-foreground"}
      `}>
        {item.title || `Step ${index + 1}`}
      </h4>
      <p className="text-xs text-muted-foreground font-body leading-relaxed line-clamp-2">
        {item.description || "Process description"}
      </p>
    </div>
  );

  if (items.length === 0) return null;

  return (
    <div className="w-full" ref={containerRef}>
      <div className="max-w-7xl mx-auto px-4">

        {/* DESKTOP VIEW - Increased gap for better arrow visibility */}
        {!isMobile && (
          <div className="flex flex-col gap-16 relative">
            {rows.map((rowItems, rowIndex) => {
              const isReverse = rowIndex % 2 !== 0;
              return (
                <div
                  key={rowIndex}
                  className={`flex items-center gap-20 lg:gap-28 ${isReverse ? "flex-row-reverse" : "flex-row"} justify-center`}
                >
                  {rowItems.map((item, colIndex) => {
                    const globalIndex = rowIndex * 3 + colIndex;
                    const isActive = globalIndex <= activeStep;
                    const isLastInRow = colIndex === rowItems.length - 1;
                    const isLastGlobal = globalIndex === items.length - 1;

                    return (
                      <div key={globalIndex} className="relative flex-1 max-w-[320px]">
                        <StepCard item={item} index={globalIndex} isActive={isActive} />

                        {/* Connector Line - Increased size for better visibility */}
                        {!isLastGlobal && (
                          <div className={`
                            absolute z-0 pointer-events-none flex items-center justify-center
                            ${isLastInRow
                              ? "top-full left-1/2 -translate-x-1/2 w-1 h-14 flex-col"
                              : isReverse
                                ? "top-1/2 right-full w-20 lg:w-24 h-0.5 flex-row-reverse"
                                : "top-1/2 left-full w-20 lg:w-24 h-0.5 flex-row"
                            }
                          `}>
                            {/* Background line */}
                            <div className={`
                              bg-border/40 relative overflow-hidden
                              ${isLastInRow ? "w-1 h-full" : "w-full h-0.5"}
                            `}>
                              {/* Animated fill line */}
                              <div
                                className={`
                                  absolute bg-gradient-to-r from-primary to-accent transition-all duration-700 ease-out
                                  ${isLastInRow
                                    ? "w-full bottom-0"
                                    : "h-full left-0"
                                  }
                                  ${globalIndex < activeStep
                                    ? isLastInRow
                                      ? "h-full"
                                      : "w-full"
                                    : isLastInRow
                                      ? "h-0"
                                      : "w-0"
                                  }
                                `}
                                style={{
                                  [isLastInRow ? 'top' : 'top']: 0,
                                  [isLastInRow ? 'left' : 'left']: 0,
                                }}
                              />
                            </div>
                            {/* Arrow indicator - Made larger and more visible */}
                            <div className={`
                              bg-white dark:bg-card rounded-full p-1.5 border-2 shadow-md transition-all duration-300
                              ${globalIndex < activeStep ? "border-primary scale-110 shadow-primary/20" : "border-border scale-100"}
                              ${isLastInRow
                                ? "rotate-90 mt-2"
                                : isReverse
                                  ? "rotate-180 -ml-2"
                                  : "-mr-2"
                              }
                            `}>
                              <ArrowRight
                                size={14}
                                className={globalIndex < activeStep ? "text-primary" : "text-muted-foreground"}
                                strokeWidth={2.5}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}

        {/* MOBILE VIEW - Increased gap */}
        {isMobile && (
          <div className="flex flex-col items-center gap-16">
            {items.map((item, index) => {
              const isActive = index <= activeStep;
              const isLast = index === items.length - 1;

              return (
                <div key={index} className="relative w-full max-w-[320px]">
                  <StepCard item={item} index={index} isActive={isActive} />

                  {!isLast && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 h-16 flex flex-col items-center">
                      <div className="w-0.5 h-full bg-border/40 relative overflow-hidden">
                        <div
                          className={`
                            absolute top-0 left-0 w-full bg-gradient-to-b from-primary to-accent
                            transition-all duration-700 ease-out
                            ${index < activeStep ? "h-full" : "h-0"}
                          `}
                        />
                      </div>
                      <div className={`
                        bg-white dark:bg-card rounded-full p-1 border-2 shadow-sm transition-all duration-300 mt-[-2px]
                        ${index < activeStep ? "border-primary scale-110" : "border-border scale-100"}
                      `}>
                        <ChevronDown
                          size={12}
                          className={index < activeStep ? "text-primary" : "text-muted-foreground"}
                          strokeWidth={2.5}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}


      </div>
    </div>
  );
};

export default SnakeFlowGallery;